/** @format */

import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ApiService from "../../services/apiService";
import { toast } from "react-toastify";
import Loader from "../../elements/Loader";

export default function PujaCalendar() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  // eslint-disable-next-line no-unused-vars
  const [_selectedDate, setSelectedDate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'view'
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDateEvents, setSelectedDateEvents] = useState([]);
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const [userName, setUserName] = useState('');

  // New state for tabs and generation selection
  const [activeTab, setActiveTab] = useState('remedies'); // 'remedies' or 'pujas'
  const [kundliRecords, setKundliRecords] = useState([]);
  const [selectedKundliId, setSelectedKundliId] = useState('all');
  const [majorPujas, setMajorPujas] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    ritual_name: '',
    ritual_description: '',
    date: '',
    day: '',
    tithi: '',
    ritual_type: '',
    rationale: '',
    isMajorPuja: false
  });

  const fetchUserName = useCallback(async () => {
    try {
      const response = await ApiService.get(`user/${userId}`);
      if (response?.result?.data) {
        setUserName(response.result.data.name || 'Unknown User');
      }
    } catch (error) {
      console.error('Failed to fetch user name:', error);
      setUserName('Unknown User');
    }
  }, [userId]);

  const fetchEvents = useCallback(async (kundliId = selectedKundliId) => {
    setLoading(true);
    try {
      let url = `kundli/remedy-recommendations/${userId}`;
      if (kundliId && kundliId !== 'all') {
        url += `?kundliId=${kundliId}`;
      }
      const response = await ApiService.get(url);
      if (response?.success && response?.data) {
        setEvents(response.data);
        setMajorPujas(response.majorPujas || []);
        // Only update kundliRecords on initial fetch (when kundliId is 'all' or first load)
        if (response?.kundliRecords && (kundliId === 'all' || kundliRecords.length === 0)) {
          setKundliRecords(response.kundliRecords);
        }
      } else {
        setEvents([]);
        setMajorPujas([]);
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
      toast.error('Failed to fetch puja events');
      setEvents([]);
      setMajorPujas([]);
    } finally {
      setLoading(false);
    }
  }, [userId, selectedKundliId, kundliRecords.length]);

  useEffect(() => {
    if (userId) {
      fetchEvents();
      fetchUserName();
    }
  }, [userId, fetchEvents, fetchUserName]);

  // Refetch when kundli selection changes
  useEffect(() => {
    if (userId && selectedKundliId) {
      fetchEvents(selectedKundliId);
    }
  }, [userId, selectedKundliId, fetchEvents]);

  const handleKundliChange = (e) => {
    setSelectedKundliId(e.target.value);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    // Format date as YYYY-MM-DD in local timezone to avoid timezone offset issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    return events.filter(event => event.date === dateStr);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleDateClick = (date) => {
    if (!date) return;
    setSelectedDate(date);
    const dateEvents = getEventsForDate(date);
    setSelectedDateEvents(dateEvents);
    setCurrentEventIndex(0);

    if (dateEvents.length > 0) {
      setSelectedEvent(dateEvents[0]);
      setModalMode('view');
    } else {
      setModalMode('create');
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      setFormData({
        ritual_name: '',
        ritual_description: '',
        date: dateStr,
        day: '',
        tithi: '',
        ritual_type: '',
        rationale: '',
        isMajorPuja: false
      });
    }
    setShowModal(true);
  };

  const navigateEvent = (direction) => {
    const newIndex = currentEventIndex + direction;
    if (newIndex >= 0 && newIndex < selectedDateEvents.length) {
      setCurrentEventIndex(newIndex);
      setSelectedEvent(selectedDateEvents[newIndex]);
    }
  };



  const handleEditEvent = (event) => {
    setSelectedEvent(event);
    setModalMode('edit');
    setFormData({
      ritual_name: event.ritual_name || '',
      ritual_description: event.ritual_description || '',
      date: event.date || '',
      day: event.day || '',
      tithi: event.tithi || '',
      ritual_type: event.ritual_type || '',
      rationale: event.rationale || '',
      isMajorPuja: event.isMajorPuja || false
    });
    setShowModal(true);
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this puja event?')) {
      return;
    }

    try {
      setLoading(true);
      await ApiService.delete(`kundli/remedy-recommendations/${eventId}`);
      toast.success('Puja event deleted successfully');
      fetchEvents();
      setShowModal(false);
    } catch (error) {
      console.error('Failed to delete event:', error);
      toast.error('Failed to delete puja event');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.ritual_name || !formData.date) {
      toast.error('Please fill in ritual name and date');
      return;
    }

    try {
      setLoading(true);

      const payload = {
        ...formData,
        userId: parseInt(userId)
      };

      if (modalMode === 'create') {
        await ApiService.post('kundli/remedy-recommendations', payload);
        toast.success('Puja event created successfully');
      } else if (modalMode === 'edit' && selectedEvent) {
        await ApiService.put(`kundli/remedy-recommendations/${selectedEvent.id}`, formData);
        toast.success('Puja event updated successfully');
      }

      fetchEvents();
      setShowModal(false);
      setFormData({
        ritual_name: '',
        ritual_description: '',
        date: '',
        day: '',
        tithi: '',
        ritual_type: '',
        rationale: '',
        isMajorPuja: false
      });
    } catch (error) {
      console.error('Failed to save event:', error);
      toast.error(`Failed to ${modalMode} puja event`);
    } finally {
      setLoading(false);
    }
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const days = getDaysInMonth(currentDate);
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  return (
    <div className="row">
      {loading && <Loader />}
      <div className="col-xl-12">
        <div className="card dz-card">
          <div className="card-header flex-wrap d-flex justify-content-between border-0">
            <div>
              <h4 className="card-title">Puja Calendar - {userName}</h4>
              <p className="text-muted mb-0">Manage puja recommendations and events</p>
            </div>
            <div className="d-flex align-items-center">
              <button
                className="btn btn-secondary me-2"
                onClick={() => navigate('/users')}
              >
                <i className="fa fa-arrow-left me-2"></i>
                Back to Users
              </button>
              <button
                className="btn btn-success"
                onClick={() => {
                  const today = new Date();
                  setSelectedDate(today);
                  setModalMode('create');
                  const year = today.getFullYear();
                  const month = String(today.getMonth() + 1).padStart(2, '0');
                  const day = String(today.getDate()).padStart(2, '0');
                  const dateStr = `${year}-${month}-${day}`;
                  setFormData({
                    ritual_name: '',
                    ritual_description: '',
                    date: dateStr,
                    day: '',
                    tithi: '',
                    ritual_type: '',
                    rationale: '',
                    isMajorPuja: false
                  });
                  setShowModal(true);
                }}
              >
                <i className="fa fa-plus me-2"></i>
                Add New Event
              </button>
            </div>
          </div>

          <div className="card-body">
            {/* Generation Dropdown and Tabs */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
              <div className="d-flex align-items-center">
                <label className="me-2 fw-bold">Select Generation:</label>
                <select
                  className="form-select"
                  value={selectedKundliId}
                  onChange={handleKundliChange}
                  style={{ width: '300px' }}
                >
                  <option value="all">All Generations</option>
                  {kundliRecords.map((kundli) => (
                    <option key={kundli.id} value={kundli.id}>
                      {kundli.name} ({kundli.relationship || 'N/A'}) - DOB: {kundli.dateOfBirth || 'N/A'}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tabs */}
              <ul className="nav nav-pills">
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'remedies' ? 'active' : ''}`}
                    onClick={() => setActiveTab('remedies')}
                  >
                    <i className="fa fa-calendar me-2"></i>
                    Remedies Calendar
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'pujas' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pujas')}
                  >
                    <i className="fa fa-star me-2"></i>
                    Recommended Pujas ({majorPujas.length})
                  </button>
                </li>
              </ul>
            </div>

            {/* Tab Content */}
            {activeTab === 'remedies' ? (
              <>
                {/* Calendar Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => navigateMonth(-1)}
                  >
                    <i className="fa fa-chevron-left"></i>
                  </button>
                  <h5 className="mb-0">
                    {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </h5>
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => navigateMonth(1)}
                  >
                    <i className="fa fa-chevron-right"></i>
                  </button>
                </div>

                {/* Calendar Grid */}
                <div className="calendar-container">
                  <div className="calendar-grid">
                    {/* Day headers */}
                    <div className="calendar-header">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="calendar-day-header">
                          {day}
                        </div>
                      ))}
                    </div>

                    {/* Calendar days */}
                    <div className="calendar-body">
                      {days.map((day, index) => {
                        const dayEvents = day ? getEventsForDate(day) : [];
                        const isToday = day && day.toDateString() === new Date().toDateString();
                        const hasEvents = dayEvents.length > 0;

                        return (
                          <div
                            key={index}
                            className={`calendar-day ${!day ? 'empty' : ''} ${isToday ? 'today' : ''} ${hasEvents ? 'has-events' : ''}`}
                            onClick={() => handleDateClick(day)}
                            style={{ cursor: day ? 'pointer' : 'default' }}
                          >
                            {day && (
                              <>
                                <div className="day-number">{day.getDate()}</div>
                                {hasEvents && (
                                  <div className="events-list">
                                    {dayEvents.slice(0, 2).map((event, idx) => (
                                      <div
                                        key={event.id}
                                        className={`event-item ${event.isMajorPuja ? 'major-puja' : ''}`}
                                        title={event.ritual_description}
                                      >
                                        {event.ritual_name.length > 20
                                          ? event.ritual_name.substring(0, 20) + '...'
                                          : event.ritual_name}
                                      </div>
                                    ))}
                                    {dayEvents.length > 2 && (
                                      <div className="more-events">
                                        +{dayEvents.length - 2} more
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Legend */}
                <div className="calendar-legend mt-4">
                  <div className="d-flex align-items-center">
                    <div className="legend-item me-4">
                      <div className="legend-dot regular-puja me-2"></div>
                      <span>Regular Puja</span>
                    </div>
                    <div className="legend-item">
                      <div className="legend-dot major-puja me-2"></div>
                      <span>Major Puja</span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              /* Recommended Pujas Tab */
              <div className="recommended-pujas-container">
                {majorPujas.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="fa fa-star fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">No Recommended Pujas</h5>
                    <p className="text-muted">No puja recommendations available for the selected generation.</p>
                  </div>
                ) : (
                  <div className="row">
                    {majorPujas.map((puja, index) => {
                      // Helper to extract puja name from various possible field names
                      const getPujaName = (p) => {
                        return p.puja_name || p.pujaName || p.name || p.title ||
                          p.ritual_name || p.ritualName || p.puja ||
                          p.Name || p.Title || `Recommended Puja ${index + 1}`;
                      };
                      // Helper to extract description from various possible field names
                      const getDescription = (p) => {
                        return p.description || p.ritual_description || p.ritualDescription ||
                          p.desc || p.content || p.details || null;
                      };
                      // Helper to extract rationale/reason from various possible field names
                      const getRationale = (p) => {
                        return p.rationale || p.reason || p.why || p.purpose ||
                          p.recommendation_reason || null;
                      };
                      // Helper to extract benefits from various possible field names
                      const getBenefits = (p) => {
                        return p.benefits || p.benefit || p.advantages ||
                          p.expected_benefits || null;
                      };

                      const pujaName = getPujaName(puja);
                      const description = getDescription(puja);
                      const rationale = getRationale(puja);
                      const benefits = getBenefits(puja);

                      return (
                        <div key={index} className="col-md-6 col-lg-4 mb-4">
                          <div className="card h-100 puja-card">
                            <div className="card-header bg-gradient-primary">
                              <h6 className="mb-0 text-white">
                                <i className="fa fa-star me-2"></i>
                                {pujaName}
                              </h6>
                            </div>
                            <div className="card-body">
                              {description && (
                                <p className="card-text text-muted mb-3">{description}</p>
                              )}
                              {rationale && (
                                <div className="mb-2">
                                  <strong>Why Recommended:</strong>
                                  <p className="text-muted small mb-0">{rationale}</p>
                                </div>
                              )}
                              {benefits && (
                                <div className="mb-2">
                                  <strong>Benefits:</strong>
                                  <p className="text-muted small mb-0">{benefits}</p>
                                </div>
                              )}
                              {(puja.timing || puja.time || puja.recommended_time) && (
                                <div className="d-flex align-items-center mt-2">
                                  <i className="fa fa-clock text-primary me-2"></i>
                                  <span className="small">{puja.timing || puja.time || puja.recommended_time}</span>
                                </div>
                              )}
                              {(puja.planet || puja.graha || puja.associated_planet) && (
                                <div className="d-flex align-items-center mt-2">
                                  <i className="fa fa-globe text-warning me-2"></i>
                                  <span className="small">Associated Planet: {puja.planet || puja.graha || puja.associated_planet}</span>
                                </div>
                              )}
                              {(puja.deity || puja.devata || puja.god) && (
                                <div className="d-flex align-items-center mt-2">
                                  <i className="fa fa-om text-success me-2"></i>
                                  <span className="small">Deity: {puja.deity || puja.devata || puja.god}</span>
                                </div>
                              )}
                              {/* Debug: Show raw puja data structure */}
                              <details className="mt-3">
                                <summary className="text-muted small" style={{ cursor: 'pointer' }}>
                                  View Raw Data
                                </summary>
                                <pre className="bg-light p-2 mt-2 rounded small" style={{ maxHeight: '200px', overflow: 'auto' }}>
                                  {JSON.stringify(puja, null, 2)}
                                </pre>
                              </details>
                            </div>
                            {(puja.priority || puja.importance) && (
                              <div className="card-footer">
                                <span className={`badge ${(puja.priority || puja.importance) === 'high' ? 'bg-danger' : (puja.priority || puja.importance) === 'medium' ? 'bg-warning' : 'bg-info'}`}>
                                  {(puja.priority || puja.importance).charAt(0).toUpperCase() + (puja.priority || puja.importance).slice(1)} Priority
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <>
          <div className="puja-modal-backdrop" onClick={() => setShowModal(false)}></div>
          <div className="puja-modal" tabIndex="-1" onClick={() => setShowModal(false)}>
            <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header">
                  <div className="d-flex align-items-center w-100">
                    {modalMode === 'view' && selectedDateEvents.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary me-2"
                        onClick={() => navigateEvent(-1)}
                        disabled={currentEventIndex === 0}
                      >
                        <i className="fa fa-chevron-left"></i>
                      </button>
                    )}

                    <h5 className="modal-title flex-grow-1">
                      {modalMode === 'create' ? 'Create Puja Event' :
                        modalMode === 'edit' ? 'Edit Puja Event' :
                          `View Puja Event${selectedDateEvents.length > 1 ? ` (${currentEventIndex + 1} of ${selectedDateEvents.length})` : ''}`}
                    </h5>

                    {modalMode === 'view' && selectedDateEvents.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary me-2"
                        onClick={() => navigateEvent(1)}
                        disabled={currentEventIndex === selectedDateEvents.length - 1}
                      >
                        <i className="fa fa-chevron-right"></i>
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowModal(false)}
                  ></button>
                </div>

                {modalMode === 'view' && selectedEvent ? (
                  <div className="modal-body">
                    <div className="row">
                      <div className="col-12">
                        <h6 className="text-primary">{selectedEvent.ritual_name}</h6>
                        {selectedEvent.isMajorPuja && (
                          <span className="badge bg-warning text-dark mb-2">Major Puja</span>
                        )}
                        <p className="text-muted mb-2">
                          <strong>Date:</strong> {formatDate(new Date(selectedEvent.date))}
                        </p>
                        {selectedEvent.day && (
                          <p className="text-muted mb-2">
                            <strong>Day:</strong> {selectedEvent.day}
                          </p>
                        )}
                        {selectedEvent.tithi && (
                          <p className="text-muted mb-2">
                            <strong>Tithi:</strong> {selectedEvent.tithi}
                          </p>
                        )}
                        {selectedEvent.ritual_type && (
                          <p className="text-muted mb-2">
                            <strong>Ritual Type:</strong> {selectedEvent.ritual_type}
                          </p>
                        )}
                        {selectedEvent.ritual_description && (
                          <>
                            <p><strong>Description:</strong></p>
                            <p className="text-muted">{selectedEvent.ritual_description}</p>
                          </>
                        )}
                        {selectedEvent.rationale && (
                          <>
                            <p><strong>Rationale:</strong></p>
                            <p className="text-muted">{selectedEvent.rationale}</p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Ritual Name *</label>
                            <input
                              type="text"
                              className="form-control"
                              value={formData.ritual_name}
                              onChange={(e) => setFormData({ ...formData, ritual_name: e.target.value })}
                              required
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Ritual Type</label>
                            <input
                              type="text"
                              className="form-control"
                              value={formData.ritual_type}
                              onChange={(e) => setFormData({ ...formData, ritual_type: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="mb-3">
                            <label className="form-label">Date *</label>
                            <input
                              type="date"
                              className="form-control"
                              value={formData.date}
                              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                              required
                            />
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="mb-3">
                            <label className="form-label">Day</label>
                            <input
                              type="text"
                              className="form-control"
                              value={formData.day}
                              onChange={(e) => setFormData({ ...formData, day: e.target.value })}
                              placeholder="e.g., Monday"
                            />
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="mb-3">
                            <label className="form-label">Tithi</label>
                            <input
                              type="text"
                              className="form-control"
                              value={formData.tithi}
                              onChange={(e) => setFormData({ ...formData, tithi: e.target.value })}
                              placeholder="e.g., Purnima"
                            />
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="mb-3">
                            <label className="form-label">Ritual Description</label>
                            <textarea
                              className="form-control"
                              rows="3"
                              value={formData.ritual_description}
                              onChange={(e) => setFormData({ ...formData, ritual_description: e.target.value })}
                              placeholder="Describe the ritual procedure..."
                            ></textarea>
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="mb-3">
                            <label className="form-label">Rationale</label>
                            <textarea
                              className="form-control"
                              rows="3"
                              value={formData.rationale}
                              onChange={(e) => setFormData({ ...formData, rationale: e.target.value })}
                              placeholder="Why this ritual is recommended..."
                            ></textarea>
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="mb-3">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={formData.isMajorPuja}
                                onChange={(e) => setFormData({ ...formData, isMajorPuja: e.target.checked })}
                              />
                              <label className="form-check-label">
                                Major Puja
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-secondary me-2"
                        onClick={() => setShowModal(false)}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-primary">
                        {modalMode === 'create' ? 'Create Event' : 'Update Event'}
                      </button>
                    </div>
                  </form>
                )}

                {modalMode === 'view' && selectedEvent && (
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-danger me-2"
                      onClick={() => handleDeleteEvent(selectedEvent.id)}
                    >
                      Delete
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary me-2"
                      onClick={() => handleEditEvent(selectedEvent)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowModal(false)}
                    >
                      Close
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <style>{`
        .calendar-container {
          max-width: 100%;
          margin: 0 auto;
        }
        
        .calendar-grid {
          border: 1px solid #e9ecef;
          border-radius: 8px;
          overflow: hidden;
        }
        
        .calendar-header {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          background-color: #f8f9fa;
        }
        
        .calendar-day-header {
          padding: 12px 8px;
          text-align: center;
          font-weight: 600;
          border-right: 1px solid #e9ecef;
          color: #495057;
        }
        
        .calendar-day-header:last-child {
          border-right: none;
        }
        
        .calendar-body {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
        }
        
        .calendar-day {
          min-height: 120px;
          padding: 8px;
          border-right: 1px solid #e9ecef;
          border-bottom: 1px solid #e9ecef;
          position: relative;
          transition: background-color 0.2s;
          overflow: hidden;
        }
        
        .calendar-day:last-child {
          border-right: none;
        }
        
        .calendar-day:hover:not(.empty) {
          background-color: #f8f9fa;
        }
        
        .calendar-day.empty {
          background-color: #f8f9fa;
        }
        
        .calendar-day.today {
          background-color: #e3f2fd;
        }
        
        .calendar-day.has-events {
          background-color: #fff3e0;
        }
        
        .day-number {
          font-weight: 600;
          margin-bottom: 4px;
        }
        
        .events-list {
          margin-top: 4px;
          font-size: 10px;
        }
        
        .event-item {
          background-color: #007bff;
          color: white;
          padding: 2px 4px;
          margin-bottom: 2px;
          border-radius: 3px;
          font-size: 9px;
          line-height: 1.2;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          cursor: pointer;
        }
        
        .event-item.major-puja {
          background-color: #ffc107;
          color: #000;
          font-weight: 600;
        }
        
        .more-events {
          font-size: 8px;
          color: #666;
          text-align: center;
          padding: 1px;
          background-color: #f8f9fa;
          border-radius: 2px;
        }
        
        .calendar-legend {
          padding: 16px;
          background-color: #f8f9fa;
          border-radius: 8px;
        }
        
        .legend-item {
          display: flex;
          align-items: center;
        }
        
        .legend-dot {
          width: 16px;
          height: 16px;
          border-radius: 3px;
          display: inline-block;
        }
        
        .legend-dot.regular-puja {
          background-color: #007bff;
        }
        
        .legend-dot.major-puja {
          background-color: #ffc107;
        }
        
        .puja-modal-backdrop {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          width: 100% !important;
          height: 100% !important;
          background-color: rgba(0, 0, 0, 0.5) !important;
          z-index: 1040 !important;
        }
        
        .puja-modal {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          width: 100% !important;
          height: 100% !important;
          z-index: 1050 !important;
          overflow: auto !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        
        .puja-modal .modal-dialog {
          margin: 1.75rem auto !important;
          max-width: 800px !important;
          position: relative !important;
          pointer-events: auto !important;
          width: 90% !important;
        }

        /* Puja Card Styles */
        .puja-card {
          border: none;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          transition: transform 0.2s, box-shadow 0.2s;
        }
        
        .puja-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 5px 20px rgba(0,0,0,0.15);
        }
        
        .puja-card .card-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-bottom: none;
        }
        
        .bg-gradient-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }
        
        .recommended-pujas-container {
          min-height: 400px;
        }
        
        /* Tab Styles */
        .nav-pills .nav-link {
          border-radius: 10px;
          padding: 10px 20px;
          font-weight: 500;
          color: #495057;
          background-color: #f8f9fa;
          margin-right: 8px;
          transition: all 0.3s ease;
        }
        
        .nav-pills .nav-link:hover {
          background-color: #e9ecef;
        }
        
        .nav-pills .nav-link.active {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }
        
        /* Generation Dropdown Styles */
        .form-select {
          border-radius: 8px;
          border: 2px solid #e9ecef;
          padding: 8px 12px;
        }
        
        .form-select:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
        }
      `}</style>
    </div>
  );
}
